import { Component } from '../../scripts/component.js';
import Flickity from 'flickity';

/**
 * A custom element that displays a featured collections carousel.
 * @extends {Component}
 */
export class FeaturedCollections extends Component {
  /** @type {Flickity | null} */
  #flkty = null;
  #resizeObserver = null;

  /**
   * Initializes the component.
   */
  connectedCallback() {
    super.connectedCallback();

    // When wrapped by <filtered-product-list>, that element drives the load
    // lifecycle (template clone + Flickity init via replaceProducts). Skipping
    // the IntersectionObserver here avoids a race that would briefly show the
    // unfiltered initial markup, and avoids double-initializing Flickity.
    if (this.closest('filtered-product-list')) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.loadContent();
          this.initializeFlickity();
          this.setupEventListeners();
        }
      });
    });
    observer.observe(this);
  }

  /**
   * Cleans up the component.
   */
  disconnectedCallback() {
    this.destroyFlickity();
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }
  }

  /**
   * Loads the content
   */
  loadContent = () => {
    if (this.getAttribute('data-featured-collections-loaded')) return;
    const template = this.querySelector('template#featured-collections-template');
    if (!template) return;
    const content = template.content.cloneNode(true);
    if (!content) return;
    this.setAttribute('data-featured-collections-loaded', 'true');
    template.after(content);
  };

  /**
   * Initializes the Flickity carousel.
   */
  initializeFlickity() {
    const swiperWrapper = this.querySelector('.swiper-wrapper');
    const layoutType = this.getAttribute('data-type');

    if (!swiperWrapper) return;

    if (layoutType === 'carousel') {
      this.#flkty = new Flickity(swiperWrapper, {
        cellAlign: 'left',
        prevNextButtons: true,
        pageDots: false,
        freeScroll: false,
        contain: true,
        wrapAround: false,
        groupCells: true,
        draggable: true,
        cellSelector: '.featured-collections-cell',
        arrowShape: { x0: 10, x1: 60, y1: 50, x2: 70, y2: 40, x3: 30 },
      });

      this.applyCustomArrows();
      this.setupFlickityEvents();
    }
  }

  /**
   * Applies custom arrow styles to the Flickity carousel.
   */
  applyCustomArrows() {
    if (!this.#flkty) return;

    const arrowTemplate = this.querySelector('#flickity-custom-arrow');
    if (!arrowTemplate) return;

    const customArrowContent = arrowTemplate.content.cloneNode(true);

    const replaceSvgContent = (button) => {
      const iconDiv = button?.querySelector('.flickity-button-icon');
      if (iconDiv) {
        iconDiv.innerHTML = '';
        iconDiv.after(customArrowContent.cloneNode(true));
        iconDiv.remove();
      }
    };

    if (this.#flkty.prevButton?.element) {
      replaceSvgContent(this.#flkty.prevButton.element);
    }
    if (this.#flkty.nextButton?.element) {
      replaceSvgContent(this.#flkty.nextButton.element);
    }
  }

  /**
   * Sets up event listeners for the Flickity carousel.
   */
  setupFlickityEvents() {
    if (!this.#flkty) return;

    this.#flkty.on('staticClick', (event, pointer, cellElement) => {
      if (!this.#flkty?.isDragging && cellElement) {
        const eventTarget = event?.target;
        if (eventTarget instanceof Element) {
          const clickedInteractiveElement = eventTarget.closest(
            'wishlist-button, button, input, label, select, textarea, [role="button"]'
          );
          if (clickedInteractiveElement) return;
        }

        const link = cellElement.querySelector('a[data-product-card-link]');
        link?.click();
      }
    });
  }

  /**
   * Sets up event listeners for the component.
   */
  setupEventListeners() {
    // Setup resize observer for responsive behavior
    this.#resizeObserver = new ResizeObserver(() => {
      this.#flkty?.resize();
    });
    this.#resizeObserver.observe(this);
  }

  /**
   * Handles collection link clicks.
   * @param {Event} e - The click event.
   */
  async handleCollectionClick(e) {
    e.preventDefault();
    const clickedLink = e.target;
    if (clickedLink.classList.contains('underline')) {
      return;
    }

    // Update active state
    const collectionLinks = this.querySelectorAll('.featured-collections-link');
    collectionLinks.forEach((link) => link.classList.remove('underline'));
    clickedLink.classList.add('underline');

    // Fetch collection data
    await this.fetchCollection(clickedLink.dataset.collectionHandle);
  }

  /**
   * Fetches collection data and updates the carousel.
   * @param {string} collectionHandle - The handle of the collection to fetch.
   */
  async fetchCollection(collectionHandle) {
    const swiperWrapper = this.querySelector('.swiper-wrapper');
    const maxProducts = this.getAttribute('data-max-products');
    const maxProductsMobile = this.getAttribute('data-max-products-mobile');
    const layoutType = this.getAttribute('data-type');
    const enableProductCardCarousel = this.getAttribute('data-enable-product-card-carousel') === 'true';
    const shouldEnableProductCardCarousel = layoutType === 'grid' && enableProductCardCarousel;
    const shopAllButtons = this.querySelectorAll('.featured-collections-shop-all-button');

    if (!swiperWrapper) return;

    // Update shop all button
    shopAllButtons.forEach((button) => {
      if (collectionHandle) button.setAttribute('href', `/collections/${collectionHandle}`);
    });

    // Show loading state
    swiperWrapper.style.minHeight = `${swiperWrapper.offsetHeight}px`;
    swiperWrapper.classList.add('is-loading');

    try {
      const response = await fetch(
        `/collections/${collectionHandle}?view=featured&enable_product_card_carousel=${shouldEnableProductCardCarousel}`
      );
      const collectionHTML = await response.text();
      const collectionHTMLDoc = new DOMParser().parseFromString(collectionHTML, 'text/html');
      const productCards = collectionHTMLDoc.querySelectorAll('.product-card');

      // Process product cards
      const products = [];
      const cardClass = 'gap-0.5 mr-0.5 featured-collections-cell absolute left-0 mb-0 float-left w-[calc(100%/var(--swiper-products-visible-mobile))] md:w-[calc(100%/var(--swiper-products-visible))]';

      productCards.forEach((card, index) => {
        if (index < 10) {
          const cardWrapper = document.createElement('div');
          const layoutType = this.getAttribute('data-type');
          if (layoutType === 'carousel') {
            cardWrapper.className = cardClass;
          }
          cardWrapper.classList.add(`${index + 1 <= maxProductsMobile ? 'block' : 'hidden'}`, `md:${index + 1 <= maxProducts ? 'block' : 'hidden'}`);
          cardWrapper.appendChild(card.cloneNode(true));
          products.push(cardWrapper);
        }
      });

      // Update carousel
      this.destroyFlickity();
      swiperWrapper.innerHTML = '';
      products.forEach((product) => swiperWrapper.appendChild(product));
      this.initializeFlickity();
    } catch (error) {
      console.error('Error fetching collection:', error);
    } finally {
      // Reset loading state
      swiperWrapper.style.minHeight = '';
      swiperWrapper.classList.remove('is-loading');
    }
  }

  /**
   * Replaces the products in the swiper with a new set of product cards.
   * Used by `<filtered-product-list>` after it fetches a filtered collection.
   * @param {Element[]} productCards - Product card nodes (e.g. <product-card>) to insert.
   */
  replaceProducts(productCards) {
    const swiperWrapper = this.querySelector('.swiper-wrapper');
    if (!swiperWrapper) return;

    const maxProducts = parseInt(this.getAttribute('data-max-products'), 10) || 5;
    const maxProductsMobile = parseInt(this.getAttribute('data-max-products-mobile'), 10) || 2;
    const layoutType = this.getAttribute('data-type');
    const cardClass = 'gap-0.5 mr-0.5 featured-collections-cell absolute left-0 mb-0 float-left w-[calc(100%/var(--swiper-products-visible-mobile))] md:w-[calc(100%/var(--swiper-products-visible))]';

    const wrappers = [];
    productCards.forEach((card, index) => {
      if (index >= 10) return;
      const cardWrapper = document.createElement('div');
      if (layoutType === 'carousel') {
        cardWrapper.className = cardClass;
      }
      cardWrapper.classList.add(
        index + 1 <= maxProductsMobile ? 'block' : 'hidden',
        `md:${index + 1 <= maxProducts ? 'block' : 'hidden'}`
      );
      cardWrapper.appendChild(card.cloneNode(true));
      wrappers.push(cardWrapper);
    });

    this.destroyFlickity();
    swiperWrapper.innerHTML = '';
    wrappers.forEach((wrapper) => swiperWrapper.appendChild(wrapper));
    swiperWrapper.classList.remove('is-loading');
    this.initializeFlickity();
  }

  /**
   * Destroys the Flickity instance.
   */
  destroyFlickity() {
    const swiperWrapper = this.querySelector('.swiper-wrapper');
    if (swiperWrapper && this.#flkty) {
      this.#flkty.destroy();
      this.#flkty = null;
    }
  }
}

// Register the custom element
if (!customElements.get('featured-collections')) {
  customElements.define('featured-collections', FeaturedCollections);
}
